import React from 'react';
import { Autocomplete, Button, Card, CardContent, Chip, Grid, Modal, Stack, TextField, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from '@mui/material';
import config from 'src/config';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import showLoading from 'src/common/loading';
import { addContainer, getContainer, updateContainer } from 'src/services/container.service';
import PersonIcon from '@mui/icons-material/Person';
import { ColorPalette } from 'mui-color';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ContainerAuditLogModal from 'src/components/container-audit-log/ContainerAuditLog.modal';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoveUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoveDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CommentModal from 'src/components/comment/Comment.modal';
import { getContacts } from 'src/services/contact.service';

// ----------------------------------------------------------------------

const containerTypeMap = {
    'container': {
        name: 'Group',
        name_plural: 'Groups'
    },
    'department': {
        name: 'Department',
        name_plural: 'Departments'
    },
    'team': {
        name: 'Team',
        name_plural: 'Teams'
    },
    'project': {
        name: 'Project',
        name_plural: 'Projects'
    }
}

const WorkflowSection = ({ workflowSteps, onWorkflowStepsChange, disabled = false }) => {
    const [showDialog, setShowDialog] = React.useState(false);
    const [editingIndex, setEditingIndex] = React.useState(null);
    const [formData, setFormData] = React.useState({ name: '', users: [] });
    const [draggedIndex, setDraggedIndex] = React.useState(null);
    const [dragOverIndex, setDragOverIndex] = React.useState(null);

    const handleAddWorkflowStep = () => {
        setFormData({ name: '', users: [] });
        setEditingIndex(null);
        setShowDialog(true);
    };

    const handleEditWorkflowStep = (index, workflow) => {
        setFormData({ name: workflow.name, users: [...workflow.users] });
        setEditingIndex(index);
        setShowDialog(true);
    };

    const handleDeleteWorkflowStep = (index) => {
        Swal.fire({
            title: 'Delete Step?',
            text: 'Are you sure you want to delete this step?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                const newWorkflows = workflowSteps.filter((_, i) => i !== index);
                onWorkflowStepsChange(newWorkflows);
            }
        });
    };

    const handleMoveUp = (index) => {
        if (index > 0) {
            const newWorkflows = [...workflowSteps];
            [newWorkflows[index], newWorkflows[index - 1]] = [newWorkflows[index - 1], newWorkflows[index]];
            onWorkflowStepsChange(newWorkflows);
        }
    };

    const handleMoveDown = (index) => {
        if (index < workflowSteps.length - 1) {
            const newWorkflows = [...workflowSteps];
            [newWorkflows[index], newWorkflows[index + 1]] = [newWorkflows[index + 1], newWorkflows[index]];
            onWorkflowStepsChange(newWorkflows);
        }
    };

    // Drag and drop handlers
    const handleDragStart = (e, index) => {
        if (disabled) return;
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
        e.target.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragOver = (e, index) => {
        if (disabled || draggedIndex === null || draggedIndex === index) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = (e) => {
        // Only clear if leaving the card entirely
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverIndex(null);
        }
    };

    const handleDrop = (e, index) => {
        if (disabled || draggedIndex === null || draggedIndex === index) return;
        e.preventDefault();
        
        const newWorkflowSteps = [...workflowSteps];
        const [removed] = newWorkflowSteps.splice(draggedIndex, 1);
        newWorkflowSteps.splice(index, 0, removed);
        onWorkflowStepsChange(newWorkflowSteps);
        
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleSaveWorkflowSteps = () => {
        if (!formData.name.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Step name is required'
            });
            return;
        }

        const newWorkflows = [...workflowSteps];
        if (editingIndex !== null) {
            newWorkflows[editingIndex] = { ...formData };
        } else {
            newWorkflows.push({ ...formData });
        }
        onWorkflowStepsChange(newWorkflows);
        setShowDialog(false);
    };

    const handleUserChange = (event, newValue) => {
        setFormData({ ...formData, users: newValue });
    };

    return (
        <>
            <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h6" color="textSecondary">
                        Workflow
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddWorkflowStep}
                        disabled={disabled}
                    >
                        Add Step
                    </Button>
                </Stack>

                {workflowSteps.length !== 0 ? (
                    <Stack direction="column" spacing={1}>
                        {workflowSteps.map((workflow, index) => (
                            <Card 
                                key={index} 
                                variant="outlined" 
                                sx={{ 
                                    p: 1.5,
                                    opacity: draggedIndex === index ? 0.5 : 1,
                                    borderColor: dragOverIndex === index ? 'primary.main' : 'inherit',
                                    borderWidth: dragOverIndex === index ? 2 : 1,
                                    transition: 'all 0.2s ease',
                                    cursor: disabled ? 'default' : 'grab'
                                }}
                                draggable={!disabled}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {/* Drag handle */}
                                    <IconButton
                                        size="small"
                                        disabled
                                        sx={{ mr: 0.5, color: 'textSecondary' }}
                                    >
                                        <DragIndicatorIcon />
                                    </IconButton>

                                    <Stack direction="column" flex={1} spacing={0.5} sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                                            {workflow.name}
                                        </Typography>
                                        {workflow.users && workflow.users.length > 0 && (
                                            <Typography variant="caption" color="textSecondary">
                                                Assigned to: {workflow.users.join(', ')}
                                            </Typography>
                                        )}
                                    </Stack>

                                    {/* Order controls */}
                                    <Stack direction="row" spacing={0.5}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveUp(index)}
                                            disabled={index === 0 || disabled}
                                            aria-label="Move up"
                                        >
                                            <MoveUpIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveDown(index)}
                                            disabled={index === workflowSteps.length - 1 || disabled}
                                            aria-label="Move down"
                                        >
                                            <MoveDownIcon fontSize="small" />
                                        </IconButton>

                                        {/* Edit/Delete */}
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditWorkflowStep(index, workflow)}
                                            disabled={disabled}
                                            aria-label="Edit workflow"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteWorkflowStep(index)}
                                            disabled={disabled}
                                            aria-label="Delete workflow"
                                            color="error"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            </Card>
                        ))}
                    </Stack>
                ) : null}
            </Stack>

            <Dialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingIndex !== null ? 'Edit Step' : 'Add Step'}
                </DialogTitle>
                <DialogContent>
                    <Stack direction="column" spacing={2} sx={{ mt: 1, minWidth: 300 }}>
                        <TextField
                            label="Step Name"
                            variant="outlined"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            autoFocus
                            size="small"
                        />
                        <Autocomplete
                            freeSolo
                            multiple
                            id="workflow-users"
                            options={formData.users}
                            value={formData.users}
                            onChange={handleUserChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Assigned Users"
                                    placeholder="Add user emails (dMonk registered users only)"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {params.InputProps.endAdornment}
                                                <PersonIcon />
                                            </React.Fragment>
                                        )
                                    }}
                                />
                            )}
                            sx={{ width: '100%' }}
                            size="small"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleSaveWorkflowSteps}>
                        {editingIndex !== null ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

class AddEditContainerModal extends React.Component {

	constructor(props) {
		super(props);

		this.state = {

			role: localStorage.getItem('role') || config.USER_ROLES.USER,
			mode: 'add',

			container: null,
			type: props?.payload?.type || 'container',
			name: '',
			shared_users: [],
            contacts: [],
			color: '#ffffff',
			workflowSteps: [],
			showWorkflowDialog: false,
			editingWorkflowIndex: null,

		}
	}

	getMapValue = (key) => {
        return containerTypeMap[this.state.type][key] || '';
    }

	// promised setState
	setStateAsync(state) {
		return new Promise((resolve) => {
			this.setState(state, resolve)
		});
	}

    async populateContacts(search='') {
        let contactsRes = await getContacts({ search });
        if (contactsRes.error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: contactsRes.error
            });
            return;
        }
        const contactsUsernames = contactsRes?.contacts?.map(contact => contact.user.username || '') || [];
        this.setState({ contacts: contactsUsernames });
    }

    getUniqueValuesInArray(array) {
        return [...new Set(array)];
    }

	async componentDidMount() {
		if (this.props.container) {


			let container = await getContainer(this.props.container._id);
			if (container.error) {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: container.error
				});
				return;
			}
			container = container.container;

			let shared_users = container?.shared_users || [];
			if (shared_users.length > 0) {
				shared_users = shared_users.map(user => user.username);
			}

			let workflowSteps = container?.workflow_steps || [];
			for (let i = 0; i < workflowSteps.length; i++) {
				if (!workflowSteps[i].users || workflowSteps[i].users.length === 0) {
					workflowSteps[i].users = [];
				}
				workflowSteps[i].users = workflowSteps[i].users.map(user => user.username);
			}

			await this.setStateAsync({
				mode: 'edit',
				container: container,
				type: container?.type || 'container',
				name: container?.name || '',
				shared_users: shared_users,
				color: container?.color || '#ffffff',
				workflowSteps: workflowSteps
			});

            await this.populateContacts();
		}
	}

	isFormValid() {
		if (!this.state.name || this.state.name.trim() === '') {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'Name is required'
			});
			return false;
		}
		return true;
	}

	async addContainer() {

		if (!this.isFormValid())
			return;

		showLoading(true);
		let addContainerRes = await addContainer({
			type: this.state.type,
			name: this.state.name,
			shared_users: this.state.shared_users,
			color: `${this.state.color}`,
			workflow_steps: this.state.workflowSteps
		});
		if (addContainerRes.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: addContainerRes.error
			});
		} else {
			Swal.fire({
				icon: 'success',
				title: 'Success',
				text: `${this.getMapValue('name')} created successfully`
			});
			this.setState({
				name: ''
			});
			if (this.props.onSave)
				this.props.onSave();
		}
	}

	async editContainer() {
		if (!this.isFormValid())
			return;

		showLoading(true);
		let addContainerRes = await updateContainer(this.props.container._id, {
			type: this.state.type,
			name: this.state.name,
			shared_users: this.state.shared_users,
			color: `${this.state.color}`,
			workflow_steps: this.state.workflowSteps
		});
		if (addContainerRes.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: addContainerRes.error
			});
		} else {
			Swal.fire({
				icon: 'success',
				title: 'Success',
				text: `${this.getMapValue('name')} updated successfully`
			});
			this.setState({
				name: ''
			});
			if (this.props.onSave)
				this.props.onSave();
		}
	}

	async saveContainer() {
		if (this.state.container) {
			await this.editContainer();
		} else {
			await this.addContainer();
		}
	}

	render() {
		return (
			<Modal
				open={this.props.open}
				onClose={() => {

					if (this.props.onClose)
						this.props.onClose();
				}}
			>
				<Grid container>
					<Grid item style={modalStyle} xs={12} md={8} lg={6}>
						<Card variant='outlined' sx={{ backgroundColor: this.state.color }}>
							<CardContent style={{ padding: 20 }}>
								<Stack direction="column" spacing={2}>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <TextField
                                            size='small'
                                            label={`${this.getMapValue('name')} Name`}
                                            variant="outlined"
                                            sx={{ flex: 1 }}
                                            value={this.state.name}
                                            onChange={async (e) => {
                                                await this.setStateAsync({
                                                    name: e.target.value
                                                });
                                            }}
                                            disabled={(this.state.mode == 'edit' && !this.state?.container?.is_owner) || (this.state?.container?.automatically_created)}
                                        />
                                        <ColorPalette
                                            palette={{
                                                white: 'white',
                                                lightyellow: 'lightyellow',
                                                lightcyan: 'lightcyan',
                                                lightgray: 'lightgray',
                                                lightblue: 'lightblue',
                                                lightgreen: 'lightgreen',
                                                beige: 'beige'
                                            }}
                                            onSelect={(color) => {
                                                this.setState({ color });
                                            }}
                                        />
                                    </Stack>

                                    <Autocomplete
										freeSolo
										multiple
										id="shared-users-outlined"
										options={this.getUniqueValuesInArray([...this.state.contacts, ...this.state.shared_users])}
										value={this.state.shared_users}
										onChange={(event, newValue) => {
											this.setState({
												shared_users: newValue
											});
										}}
										renderInput={(params) => (
                                            <TextField
												{...params}
												variant="outlined"
												label="Shared with"
												placeholder={((this.state.mode == 'add') || (this.state.mode == 'edit' && this.state?.container?.is_owner)) ? "Add user emails ( dMonk registered users only )" : ""}
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<React.Fragment>
															{params.InputProps.endAdornment}
															<PersonIcon />
														</React.Fragment>
													)
												}}
                                                onChange={async (event) => {
                                                    this.populateContacts(event.target.value);
                                                }}
											/>
										)}
										sx={{ width: '100%' }}
										size="small"
										disabled={this.state.mode == 'edit' && !this.state?.container?.is_owner}
									/>

                                    <div>
                                        <CommentModal
                                            container={this.state.container}
                                            is_modal_open={this.props.openComment}
                                        />
                                    </div>

									{((this.state.mode == 'add') || (this.state.mode == 'edit' && this.state?.container?.is_owner)) && (
                                        <WorkflowSection
                                            workflowSteps={this.state.workflowSteps}
                                            onWorkflowStepsChange={(workflowSteps) => {
                                                this.setState({ workflowSteps })
                                                const uniqueUsers = new Set();
                                                workflowSteps.forEach(step => {
                                                    step.users.forEach(user => uniqueUsers.add(user));
                                                });
                                                const existingSharedUsers = new Set(this.state.shared_users);
                                                const newSharedUsers = Array.from(uniqueUsers).filter(user => !existingSharedUsers.has(user));
                                                if (newSharedUsers.length > 0) {
                                                    this.setState(prevState => ({
                                                        shared_users: [...prevState.shared_users, ...newSharedUsers]
                                                    }));
                                                }
                                            }}
                                        />
									)}
									{((this.state.mode == 'add') || (this.state.mode == 'edit' && this.state?.container?.is_owner)) && (
										<>
										<Stack direction={'row'} spacing={2} alignItems={'center'}>
											<Button
												variant='contained'
												fullWidth
												color='primary'
												onClick={async () => {
													this.saveContainer();
												}}
											>
												Save
											</Button>
											<ContainerAuditLogModal
												view_type='icon'
												container={this.state.container}
											/>
										</Stack>
										</>
									)}
								</Stack>


								{/* Owners */}
								{(!this.state.container?.is_owner && this.state.container?.owner) ? (
									<Grid container spacing={2} sx={{ mt: 1 }}>
										<Grid item xs={12}>
											<Stack spacing={1} direction={'row'}>
												{/* Icon */}
												<ShieldOutlinedIcon style={{ color: 'gray' }} />
												{/* owner */}
												{(this.state.container?.owner) ? (
													<Stack spacing={1} direction={'row'}>
														<Chip
															size="small"
															label={this.state.container?.owner?.name ? (this.state.container?.owner?.name + ' (' + this.state.container?.owner?.username + ')') : this.state.container?.owner?.username}
															variant="outlined"
															sx={{m: 1, borderRadius: '5px'}}
															disabled
														/>
													</Stack>
												) : null}
											</Stack>
										</Grid>
									</Grid>
								) : null}

							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Modal>
		);
	}
}

const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '100%',
	bgcolor: 'background.paper',
	boxShadow: 24,
	p: 2,
	maxHeight: '90%',
	overflowY: 'auto'
};

export default function (props) {
	const navigate = useNavigate();

	return <AddEditContainerModal {...props} navigate={navigate} />;
}
